import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Modal from '../../../incase-domain/components/modals/directory/Modal';
import {IElementImageMarkup, IPoint, IShape} from '../../../incase-domain/elements/question/imageMarkup';
import Button from '../../../incase-domain/components/button/Button';
import {useSetHighlightedElement} from '../../../utils/hooks/slideUpdates/useSetSlideElement';
import useElementActions from '../../../utils/hooks/slideUpdates/useElementActions';

interface ImageMarkupModalProps {
    element: IElementImageMarkup;
    isOpen: boolean;
    onCancel: () => void;
}

function ImageMarkupModal({element, isOpen, onCancel}: ImageMarkupModalProps): JSX.Element {
    const {t} = useTranslation();
    const imageRef = useRef<HTMLImageElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const [shapes, setShapes] = useState<IShape[]>(element.markedAreas || []);
    const [isDrawing, setIsDrawing] = useState(false);
    const [canvDimensions, setCanvDimensions] = useState<{width: number; height: number}>({width: 0, height: 0});

    const updateHighlightedElement = useSetHighlightedElement();
    const {deleteElement} = useElementActions();

    useEffect(() => {
        if (isOpen) {
            setShapes(element.markedAreas);
        }
    }, [isOpen, element.markedAreas]);

    useEffect(() => {
        if (imageRef.current) {
            const img = imageRef.current;

            const updateDimensions = (): void => {
                const container = {
                    width: img.width,
                    height: img.height,
                };

                const imageRatio = img.naturalWidth / img.naturalHeight;
                const containerRatio = container.width / container.height;

                let actualWidth;
                let actualHeight;

                if (imageRatio > containerRatio) {
                    actualWidth = container.width;
                    actualHeight = container.width / imageRatio;
                } else {
                    actualHeight = container.height;
                    actualWidth = container.height * imageRatio;
                }

                setCanvDimensions({width: actualWidth, height: actualHeight});
            };

            img.addEventListener('load', updateDimensions);
            if (img.complete) updateDimensions();

            return () => img.removeEventListener('load', updateDimensions);
        }

        return undefined;
    }, [element.imageSrc, isOpen]);

    useEffect(() => {
        if (element.markedAreas) {
            setShapes(element.markedAreas);
        }
    }, [element.markedAreas]);

    useEffect(() => {
        if (canvas && ctx) {
            const {width, height} = canvDimensions;
            ctx.clearRect(0, 0, width, height);
            if (shapes) {
                shapes.forEach((shape: IShape) => {
                    if (shape.points.length < 2) return;
                    ctx.beginPath();
                    ctx.moveTo(shape.points[0].x * width, shape.points[0].y * height);
                    shape.points.forEach((point: IPoint) => {
                        ctx.lineTo(point.x * width, point.y * height);
                    });
                    ctx.fillStyle = 'rgba(255, 0, 0, 0.2)';
                    ctx.fill();
                    ctx.strokeStyle = 'purple';
                    ctx.lineWidth = 2;
                    ctx.stroke();
                });
            }
        }
    }, [canvDimensions, canvDimensions.height, canvDimensions.width, canvas, ctx, shapes, shapes.length]);

    const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>): void => {
        setIsDrawing(true);
        if (canvas) {
            const rect = canvas.getBoundingClientRect();
            const x = parseFloat(((e.clientX - rect.left) / canvas.width).toFixed(3));
            const y = parseFloat(((e.clientY - rect.top) / canvas.height).toFixed(3));

            setShapes([...shapes, {points: [{x, y}]}]);
        }
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>): void => {
        if (isDrawing && canvas && shapes) {
            const rect = canvas.getBoundingClientRect();
            const x = parseFloat(((e.clientX - rect.left) / canvas.width).toFixed(3));
            const y = parseFloat(((e.clientY - rect.top) / canvas.height).toFixed(3));

            const currentShape = shapes[shapes.length - 1];
            currentShape.points.push({x, y});
            setShapes([...shapes.slice(0, shapes.length - 1), currentShape]);
        }
    };

    const handleMouseUp = (): void => {
        setIsDrawing(false);
        if (shapes.length > 0) {
            const currentShape = shapes[shapes.length - 1];
            if (currentShape?.points?.length > 2) {
                currentShape.points = [...currentShape.points, currentShape.points[0]];
            }
            setShapes([...shapes.slice(0, shapes.length - 1), currentShape]);
        }
    };

    const handleCancel = (): void => {
        if (!element.markedAreas.length) {
            deleteElement(element.elementIndex);
        }
        onCancel();
    };

    const handleMarkImageArea = (): void => {
        if (!shapes.length) {
            deleteElement(element.elementIndex);
        } else {
            updateHighlightedElement.single('markedAreas', shapes);
            onCancel();
        }
    };

    return (
        <Modal
            header={t('designSidepanel.answerMarkArea')}
            description={`${t('designSidepanel.selectCorrectAreaOnImage')}`}
            icon="solar:ruler-cross-pen-bold"
            isOpen={isOpen}
            onCancel={handleCancel}
            onConfirm={handleMarkImageArea}
            showConfirm
            confirmText={t('common.save')}
            showCancel
            cancelText={t('common.dismiss')}
            size="fit"
            closeOnClickOutside={false}
            confirmDisabled={!shapes.length}
        >
            <div className="c-imageMarkup">
                <div className="c-imageMarkup__canvas c-imageMarkup__canvas--modal c-imageMarkup__canvas--pencil">
                    <img ref={imageRef} src={element.imageSrc} alt="" />
                    <canvas
                        width={canvDimensions.width}
                        height={canvDimensions.height}
                        ref={canvasRef}
                        onMouseDown={handleMouseDown}
                        onMouseUp={handleMouseUp}
                        onMouseMove={handleMouseMove}
                    />
                    {shapes.length > 0 && (
                        <div className="c-imageMarkup__canvas_buttonGroup">
                            <Button variant="ghost" size="md" color="secondary" onClick={() => setShapes([])}>
                                {t('common.clear')}
                            </Button>
                            <Button variant="ghost" size="md" color="secondary" onClick={() => setShapes((curr) => curr.slice(0, -1))}>
                                {t('common.undo')}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
}

export default ImageMarkupModal;
